import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';

import UserBasicInfoSection from './components/UserBasicInfoSection';
import RolePermissionsSection from './components/RolePermissionsSection';
import AccountSettingsSection from './components/AccountSettingsSection';
import FormActions from './components/FormActions';
import SuccessModal from './components/SuccessModal';

const UserCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editUser = location?.state?.editUser;
  const isEditMode = Boolean(editUser);

  // State management
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: 'viewer',
    temporaryPassword: '',
    generatePassword: true,
    sendInvitation: true,
    requirePasswordChange: true
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successModal, setSuccessModal] = useState({ isOpen: false, user: null });

  // Initialize form data for editing
  useEffect(() => {
    if (isEditMode && editUser) {
      setFormData({
        fullName: editUser?.name || '',
        email: editUser?.email || '',
        role: editUser?.role || 'viewer',
        temporaryPassword: '',
        generatePassword: false,
        sendInvitation: false,
        requirePasswordChange: false
      });
    }
  }, [isEditMode, editUser]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.role) {
      newErrors.role = 'Please select a user role';
    }

    if (!isEditMode) {
      if (!formData?.generatePassword && !formData?.temporaryPassword?.trim()) {
        newErrors.temporaryPassword = 'Please provide a temporary password or enable auto-generation';
      } else if (formData?.temporaryPassword && formData?.temporaryPassword?.length < 8) {
        newErrors.temporaryPassword = 'Password must be at least 8 characters long';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Generate secure password
  const generateSecurePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    return password;
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Auto-generate password when option is enabled
    if (field === 'generatePassword' && value) {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        temporaryPassword: generateSecurePassword()
      }));
    }
  };

  // Check email uniqueness
  const checkEmailUniqueness = async (email) => {
    // Simulate API call to check email uniqueness
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock existing emails for validation
    const existingEmails = [
      'admin@party.com',
      'sarah.johnson@party.com',
      'michael.rodriguez@party.com'
    ];
    
    // If editing, exclude current user's email
    const emailsToCheck = isEditMode 
      ? existingEmails?.filter(e => e !== editUser?.email)
      : existingEmails;
    
    return !emailsToCheck?.includes(email?.toLowerCase());
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      // Check email uniqueness
      const isEmailUnique = await checkEmailUniqueness(formData?.email);
      if (!isEmailUnique) {
        setErrors({ email: 'This email address is already in use' });
        return;
      }

      // Generate password if needed
      let finalPassword = formData?.temporaryPassword;
      if (!isEditMode && formData?.generatePassword) {
        finalPassword = generateSecurePassword();
      }

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      const userData = {
        id: isEditMode ? editUser?.id : Date.now(),
        name: formData?.fullName?.trim(),
        email: formData?.email?.toLowerCase()?.trim(),
        role: formData?.role,
        status: 'active',
        lastLogin: null,
        createdAt: isEditMode ? editUser?.createdAt : new Date()?.toISOString(),
        ...(isEditMode ? {} : { temporaryPassword: finalPassword })
      };

      // Show success modal
      setSuccessModal({ isOpen: true, user: userData });

    } catch (error) {
      setErrors({ 
        submit: isEditMode 
          ? 'Failed to update user. Please try again.' :'Failed to create user. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Handle successful creation/update
  const handleSuccess = (action) => {
    setSuccessModal({ isOpen: false, user: null });
    
    if (action === 'create_another') {
      // Reset form for creating another user
      setFormData({
        fullName: '',
        email: '',
        role: 'viewer',
        temporaryPassword: '',
        generatePassword: true,
        sendInvitation: true,
        requirePasswordChange: true
      });
      setErrors({});
    } else {
      // Navigate back to user management
      navigate('/user-management');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {isEditMode ? 'Edit User' : 'Create New User'}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode 
                ? 'Update user information and role assignments'
                : 'Add a new user account with appropriate role assignments and access permissions'
              }
            </p>
          </div>

          <div className="space-y-8">
            {/* Basic Information */}
            <UserBasicInfoSection
              formData={formData}
              errors={errors}
              onFieldChange={handleFieldChange}
              isEditMode={isEditMode}
            />

            {/* Role & Permissions */}
            <RolePermissionsSection
              formData={formData}
              errors={errors}
              onFieldChange={handleFieldChange}
            />

            {/* Account Settings - Only show for new users */}
            {!isEditMode && (
              <AccountSettingsSection
                formData={formData}
                errors={errors}
                onFieldChange={handleFieldChange}
                onGeneratePassword={() => {
                  let password = generateSecurePassword();
                  handleFieldChange('temporaryPassword', password);
                }}
              />
            )}

            {/* Form Actions */}
            <FormActions
              onSubmit={handleSubmit}
              onCancel={() => navigate('/user-management')}
              isSubmitting={isSubmitting}
              isEditMode={isEditMode}
              submitError={errors?.submit}
            />
          </div>
        </div>
      </main>

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModal?.isOpen}
        onClose={() => setSuccessModal({ isOpen: false, user: null })}
        onAction={handleSuccess}
        user={successModal?.user}
        isEditMode={isEditMode}
      />

      {/* Loading Overlay */}
      <LoadingOverlay
        isVisible={loading}
        message={isEditMode ? "Atualizando usuário..." : "Criando conta de usuário..."}
      />
    </div>
  );
};

export default UserCreation;